import { useState } from 'react';
import { Card } from '@design/Card';
import { Button } from '@design/Button';
import { useEventBus } from '@bus/useEventBus';
import type { VideoMeta } from '@curriculum/useChapterVideo';
import { getChannel } from '@curriculum/useChapterVideo';

/**
 * Lazy YouTube embed with poster fallback. No iframe loads until the user
 * clicks Play — preserves Lighthouse + 4G mobile. When the video isn't yet on
 * YouTube (youtubeId === null) we show the poster + a "uploading to our
 * channel" affordance with a link to the channel itself.
 *
 * Inspired by the lite-youtube-embed pattern but hand-rolled to avoid the
 * extra dependency.
 */
export function ChapterVideo({ chapterId, video }: { chapterId: string; video: VideoMeta }) {
  const emit = useEventBus();
  const [playing, setPlaying] = useState(false);
  const channel = getChannel();
  const minutes = Math.floor(video.durationSeconds / 60);
  const seconds = Math.round(video.durationSeconds % 60);
  const durationLabel = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const liveOnYouTube = !!video.youtubeId;

  function play() {
    if (!liveOnYouTube) return;
    setPlaying(true);
    emit({ kind: 'dialogue.advance', payload: { simId: `video:${chapterId}`, step: 1, direction: 'next' } });
  }

  return (
    <Card className="!p-0 overflow-hidden shadow-card-raised !rounded-card-lg">
      <div className="px-5 sm:px-6 py-4 border-b border-ink-200/80 bg-cream flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] text-accent-600 font-semibold">Watch — {durationLabel}</div>
          <h2 className="font-serif text-lg sm:text-xl text-ink-900 mt-1 leading-snug text-balance">{video.title}</h2>
        </div>
        <a
          href={channel.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-ink-500 hover:text-ink-900 font-medium whitespace-nowrap underline-offset-4 hover:underline"
        >
          @koshythomas →
        </a>
      </div>

      <div className="relative aspect-video bg-ink-900">
        {playing && liveOnYouTube ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <>
            <img
              src={video.thumbnail}
              alt={video.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 via-transparent to-transparent" />
            {liveOnYouTube ? (
              <button
                onClick={play}
                className="absolute inset-0 flex items-center justify-center group"
                aria-label={`Play ${video.title}`}
              >
                <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 group-hover:bg-white text-ink-900 flex items-center justify-center ring-8 ring-white/20 shadow-[0_8px_28px_rgba(0,0,0,0.45)] transition-[transform,background-color] duration-200 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-9 sm:h-9 ml-1 fill-current"><path d="M8 5v14l11-7z" /></svg>
                </span>
              </button>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-4">
                  <div className="inline-flex items-center gap-2 text-white/90 text-xs uppercase tracking-wide bg-black/40 backdrop-blur px-3 py-1.5 rounded-full border border-white/15">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                    Uploading to our YouTube channel
                  </div>
                  <p className="text-white text-sm mt-3 font-medium">
                    Subscribe to be notified when it goes live.
                  </p>
                  <a
                    href={channel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3"
                  >
                    <Button variant="primary" className="!bg-white !text-ink-900 hover:!bg-white/90">
                      Subscribe on YouTube
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {!liveOnYouTube && (
        <div className="px-5 py-2.5 bg-cream border-t border-ink-200 text-xs text-ink-500">
          The video for this chapter is fully produced — narrated, animated, captioned — and ready to publish on the Koshy Thomas Online Classes channel. Check back tomorrow.
        </div>
      )}
    </Card>
  );
}
